import { useCallback, useEffect, useState } from "react";
import { Moon, Timer, Check, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { subjectStyle, fmtDue, ukToday } from "@/lib/subjects";
import { FocusTimer } from "@/components/FocusTimer";

const KIND = { homework: "Homework", revision: "Test revision", pack: "Revision pack", exam: "Exam revision" };
const ESTIMATES = [10, 15, 20, 30, 45, 60, 90];

const tickItem = (it) => {
  if (it.type === "homework") return api.put(`/homework/${it.id}/quick`, { done: true });
  if (it.type === "revision") return api.put(`/homework/${it.id}/plan/${it.idx}`, { done: true });
  if (it.type === "pack") return api.put(`/revision/packs/${it.id}/timetable/${it.idx}`, { done: true });
  return api.put(`/exams/plan/${it.id}/${it.idx}`, { done: true });
};

const Row = ({ it, i, onTick, onFocus, onEstimate }) => (
  <li data-testid={`tonight-item-${i}`} className="relative flex gap-3 pl-1">
    <div className="flex flex-col items-center">
      <span className="font-mono text-xs font-bold text-gold-300 w-11 text-right">{it.start}</span>
      <span className="mt-1 flex-1 w-px bg-gradient-to-b from-gold-400/60 to-transparent" />
    </div>
    <div className="flex-1 min-w-0 mb-3 rounded-2xl border border-slate-200 bg-surface-2/80 p-3">
      <div className="flex items-start gap-2">
        <button data-testid={`tonight-tick-${i}`} onClick={() => onTick(it)} aria-label="Mark done" className="mt-0.5 grid place-items-center w-6 h-6 shrink-0 rounded-md border border-slate-300 hover:border-gold-400 hover:bg-gold-400/20"><Check className="w-3.5 h-3.5 text-transparent hover:text-gold-300" /></button>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-white leading-snug">{it.title}</p>
          <p className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-slate-500">
            {it.subject && <span className={`rounded-full border px-2 py-0.5 font-semibold ${subjectStyle(it.subject).chip}`}>{it.subject}</span>}
            <span>{KIND[it.type]}</span>
            {it.due && <span className={it.pri < 0 ? "text-rose-600 font-semibold" : ""}>· {it.pri < 0 ? "overdue" : `due ${fmtDue(it.due)}`}</span>}
          </p>
        </div>
        <button data-testid={`tonight-focus-${i}`} onClick={() => onFocus(it)} aria-label="Start focus timer" className="grid place-items-center w-9 h-9 shrink-0 rounded-xl border border-gold-400/50 text-gold-300 hover:bg-gold-400 hover:text-black"><Timer className="w-4 h-4" /></button>
      </div>
      <div className="mt-2 flex items-center gap-2 text-xs text-slate-500">
        {it.type === "homework" ? (
          <select data-testid={`tonight-estimate-${i}`} value={it.estimated ? it.minutes : ""} onChange={(e) => onEstimate(it, Number(e.target.value))}
            className={`h-7 rounded-full border px-2 text-xs ${it.estimated ? "border-slate-200 bg-surface text-slate-700" : "border-gold-400/60 bg-gold-400/10 text-gold-300"}`}>
            <option value="" disabled>How long? (guess 30m)</option>
            {ESTIMATES.map((m) => <option key={m} value={m}>{`${m} min`}</option>)}
          </select>
        ) : <span>{it.minutes} min</span>}
        <span>· until {it.end}</span>
      </div>
    </div>
  </li>
);

export const TonightPlan = () => {
  const [p, setP] = useState(null);
  const [focus, setFocus] = useState(null);
  const load = useCallback(() => api.get("/tonight").then((r) => setP(r.data ?? null)).catch(() => setP(null)), []);
  useEffect(() => { load(); }, [load]);
  const tick = async (it) => { await tickItem(it).catch(() => {}); load(); };
  const estimate = async (it, minutes) => { await api.put(`/homework/${it.id}/quick`, { minutes }).catch(() => {}); load(); };
  const items = Array.isArray(p?.items) ? p.items : [];
  if (!p) return null;
  return (
    <section data-testid="tonight-plan-section" className="card p-5 sm:p-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="eyebrow flex items-center gap-2"><Moon className="w-4 h-4" />Tonight's plan · {fmtDue(ukToday())}</p>
          <h2 className="mt-1 font-display text-3xl text-white">{items.length ? `Start at ${p.start}` : "Nothing to do tonight"}</h2>
        </div>
        {items.length > 0 && <p data-testid="tonight-total" className="text-sm text-slate-600">{Math.floor(p.total_minutes / 60) ? `${Math.floor(p.total_minutes / 60)}h ` : ""}{p.total_minutes % 60}m of work · done by <b className="text-white">{p.finish}</b></p>}
      </div>
      {p.late && <p data-testid="tonight-late" className="mt-3 flex items-center gap-2 rounded-xl border border-brand/50 bg-brand/10 px-3 py-2 text-xs text-slate-800"><AlertTriangle className="w-4 h-4 text-rose-600" />That runs late. Do the top items tonight and move the rest to tomorrow.</p>}
      {items.length > 0 ? <ol className="mt-4">{items.map((it, i) => <Row key={`${it.type}-${it.id}-${it.idx ?? ""}`} it={it} i={i} onTick={tick} onFocus={setFocus} onEstimate={estimate} />)}</ol>
        : <p className="mt-2 text-sm text-slate-600">No homework due soon and no revision scheduled. Enjoy your evening.</p>}
      <FocusTimer open={Boolean(focus)} onOpenChange={(o) => !o && setFocus(null)} label={focus?.title || "Focus"} minutes={25} onComplete={() => focus && tick(focus)} />
    </section>
  );
};
